import { Routes } from '@angular/router';
import { PublicLayoutComponent } from './layouts/public-layout/public-layout.component';
import { LandingPageComponent } from './shared/components/landing-page/landing-page.component';
import { LandingV2Component } from './shared/components/landing-v2/landing-v2.component';
import { LoginComponent } from './pages/login/login.component';
import { RegisterComponent } from './pages/register/register.component';
import { PricingComponent } from './shared/components/pricing/pricing.component';
import { AppLayoutComponent } from './layouts/app-layout/app-layout.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { authGuard } from './guards/auth.guard';
import { onlineGuard } from './guards/online.guard';
import { unsavedChangesGuard } from './guards/unsaved-changes.guard';
import { SubscriptionComponent } from './pages/subscription/subscription.component';
import { CheckoutComponent } from './pages/checkout/checkout.component';
import { PaymentStatusComponent } from './pages/payment-status/payment-status.component';
import { EventListComponent } from './pages/events/event-list/event-list.component';
import { CreateEventComponent } from './pages/events/create-event/create-event.component';
import { EventDetailsComponent } from './pages/events/event-details/event-details.component';
import { PostEventComponent } from './pages/events/post-event/post-event.component';
import { EventReportComponent } from './pages/events/event-report/event-report.component';
import { HistoryComponent } from './pages/history/history.component';
import { PreparationListComponent } from './pages/preparation/preparation-list/preparation-list.component';
import { PleaseVerifyComponent } from './pages/please-verify/please-verify.component';
import { VerifyEmailComponent } from './pages/verify-email/verify-email.component';
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './pages/reset-password/reset-password.component';
import { onboardingGuard } from './guards/onboarding.guard';
import { adminGuard } from './guards/admin.guard';
import { ProductsComponent } from './pages/products/products.component';
import { StepContextFixedComponent } from './pages/onboarding/step-context-fixed/step-context-fixed.component';
import { StepContextItinerantComponent } from './pages/onboarding/step-context-itinerant/step-context-itinerant.component';
import { ResumeEtPotentielComponent } from './pages/onboarding/resumeEtPotentiel/resumeEtPotentiel.component';
import { StepMerchantTypeComponent } from './pages/onboarding/step-merchant-type/step-merchant-type.component';
import { StepProductsTypeComponent } from './pages/onboarding/step-products-type/step-products-type.component';
import { DashboardItinerantComponent } from './pages/dashboard/dashboard-itinerant/dashboard-itinerant.component';
import { DashboardFixedComponent } from './pages/dashboard/dashboard-fixed/dashboard-fixed.component';
import { RecommendationsPageComponent } from './pages/recommendations-page/recommendations-page.component';
import { RecommendationsFixedComponent } from './pages/recommendations-fixed/recommendations-fixed.component';
import { RecommendationsItinerantComponent } from './pages/recommendations-itinerant/recommendations-itinerant.component';
import { RecommendationsComponent } from './pages/recommendations/recommendations.component';
import { StepProductsCatalogComponent } from './pages/onboarding/step-products-catalog/step-products-catalog.component';
import { StepProductsConfigComponent } from './pages/onboarding/step-products-config/step-products-config.component';
import { LocationStepComponent } from './pages/onboarding/location-step/location-step.component';
import { SalesViewComponent } from './kiosk/containers/sales-view/sales-view.component';
import { StatsViewComponent } from './kiosk/containers/stats-view/stats-view.component';
import { HistoryViewComponent } from './kiosk/containers/history-view/history-view.component';
import { MerchantServicesComponent } from './pages/services/merchant-services/merchant-services.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { SalesEntryFixedComponent } from './pages/sales/sales-entry-fixed/sales-entry-fixed.component';
import { SalesEntryItinerantComponent } from './pages/sales/sales-entry-itinerant/sales-entry-itinerant.component';
import { CguComponent } from './pages/public/cgu/cgu.component';
import { ConfidentialiteComponent } from './pages/public/confidentialite/confidentialite.component';
import { AboutComponent } from './pages/public/about/about.component';
import { ContactComponent } from './pages/public/contact/contact.component';
import { BlogComponent } from './pages/public/blog/blog.component';
import { GuidesComponent } from './pages/public/guides/guides.component';
import { DocsComponent } from './pages/public/docs/docs.component';
import { RoadmapComponent } from './pages/public/roadmap/roadmap.component';
import { LegalLayoutComponent } from './layouts/legal-layout/legal-layout.component';

export const routes: Routes = [
  // Section admin (propriétaire) : URL non référencée dans les menus, session admin dédiée (ADR-035)
  {
    path: 'admin/login',
    loadComponent: () =>
      import('./pages/admin/admin-login/admin-login.component').then(
        (m) => m.AdminLoginComponent,
      ),
  },
  {
    path: 'admin',
    loadComponent: () =>
      import('./layouts/admin-layout/admin-layout.component').then(
        (m) => m.AdminLayoutComponent,
      ),
    canActivate: [adminGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/admin/admin-home/admin-home.component').then(
            (m) => m.AdminHomeComponent,
          ),
      },
      {
        path: 'tax-reports',
        loadComponent: () =>
          import('./pages/admin/tax-reports/tax-reports.component').then(
            (m) => m.TaxReportsComponent,
          ),
      },
      {
        path: 'pilots',
        loadComponent: () =>
          import('./pages/admin/pilots/pilots.component').then(
            (m) => m.PilotsComponent,
          ),
      },
      {
        path: 'catalog',
        loadComponent: () =>
          import('./pages/admin/catalog/catalog.component').then(
            (m) => m.CatalogComponent,
          ),
      },
      {
        path: 'relink',
        loadComponent: () =>
          import('./pages/admin/relink/relink.component').then(
            (m) => m.RelinkComponent,
          ),
      },
    ],
  },
  {
    path: '',
    component: PublicLayoutComponent,
    children: [
      { path: '', component: LandingV2Component },
      { path: 'landing-archive', component: LandingPageComponent },
      { path: 'login', component: LoginComponent },
      { path: 'register', component: RegisterComponent },
      { path: 'pricing', component: PricingComponent },

      // Routes pour la vérification de l'e-mail
      { path: 'please-verify', component: PleaseVerifyComponent },
      { path: 'verify-email', component: VerifyEmailComponent },

      // 🔐 Routes pour la réinitialisation du mot de passe
      { path: 'forgot-password', component: ForgotPasswordComponent },
      { path: 'reset-password', component: ResetPasswordComponent },
    ],
  },
  // Pages légales/ressources : layout adaptatif (app si connecté, public sinon)
  {
    path: '',
    component: LegalLayoutComponent,
    children: [
      { path: 'cgu', component: CguComponent },
      { path: 'confidentialite', component: ConfidentialiteComponent },
      { path: 'about', component: AboutComponent },
      { path: 'contact', component: ContactComponent },
      { path: 'blog', component: BlogComponent },
      { path: 'guides', component: GuidesComponent },
      { path: 'docs', component: DocsComponent },
      { path: 'roadmap', component: RoadmapComponent },
    ],
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        component: DashboardComponent,
        canActivate: [onboardingGuard],
      },

      // Anciennes routes dashboard - redirigent vers la nouvelle
      {
        path: 'dashboard-itinerant',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      { path: 'dashboard-fixed', redirectTo: 'dashboard', pathMatch: 'full' },

      // Recommandations - route unique (affiche fixed ou itinerant selon merchantType)
      {
        path: 'recommendations',
        component: RecommendationsComponent,
        canActivate: [onboardingGuard],
      },

      // Anciennes routes - redirigent vers la nouvelle
      {
        path: 'recommandation',
        redirectTo: 'recommendations',
        pathMatch: 'full',
      },
      {
        path: 'recommendations/fixed',
        redirectTo: 'recommendations',
        pathMatch: 'full',
      },
      {
        path: 'recommendations/itinerant',
        redirectTo: 'recommendations',
        pathMatch: 'full',
      },

      // Stats et Historique Kiosk dans le layout app
      { path: 'kiosk/stats', component: StatsViewComponent },
      { path: 'kiosk/history', component: HistoryViewComponent },

      {
        path: 'subscription',
        component: SubscriptionComponent,
        canActivate: [onlineGuard],
      },
      {
        path: 'invoices',
        loadComponent: () =>
          import('./pages/invoices/invoices.component').then(
            (m) => m.InvoicesComponent,
          ),
        canActivate: [onlineGuard],
      },
      {
        path: 'billing-profile',
        loadComponent: () =>
          import('./pages/billing-profile/billing-profile.component').then(
            (m) => m.BillingProfileComponent,
          ),
        canActivate: [onlineGuard],
      },
      {
        path: 'checkout',
        component: CheckoutComponent,
        canActivate: [onlineGuard],
      },
      { path: 'payment-status', component: PaymentStatusComponent },
      { path: 'events', component: EventListComponent },
      {
        path: 'opportunities',
        loadComponent: () =>
          import('./pages/opportunities/opportunities-page.component').then(
            (m) => m.OpportunitiesPageComponent,
          ),
        canActivate: [onboardingGuard],
      },
      { path: 'events/create', component: CreateEventComponent },
      { path: 'events/post', component: PostEventComponent },
      { path: 'events/:id/edit', component: CreateEventComponent },
      { path: 'events/:id/rapport', component: EventReportComponent },
      { path: 'events/:id', component: EventDetailsComponent },
      { path: 'history', component: HistoryComponent },
      { path: 'preparation/list', component: PreparationListComponent },
      { path: 'payment-status', component: PaymentStatusComponent },
      {
        path: 'products',
        component: ProductsComponent,
        canDeactivate: [unsavedChangesGuard],
      },

      // Saisie manuelle des ventes
      {
        path: 'sales/entry-fixed',
        component: SalesEntryFixedComponent,
        canActivate: [onboardingGuard],
      },
      {
        path: 'sales/entry-itinerant',
        component: SalesEntryItinerantComponent,
        canActivate: [onboardingGuard],
      },

      // Services marchand fixe
      {
        path: 'services',
        component: MerchantServicesComponent,
        canActivate: [onboardingGuard],
      },

      // Profil marchand
      {
        path: 'profile',
        component: ProfileComponent,
        canActivate: [onboardingGuard],
      },

      // Paramètres
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/settings/settings.component').then(
            (m) => m.SettingsComponent,
          ),
        canActivate: [onboardingGuard],
      },

      // Onboarding (nouveau)
      {
        path: 'onboarding',
        redirectTo: 'onboarding/step/1',
        pathMatch: 'full',
      },
      { path: 'onboarding/step/1', component: StepMerchantTypeComponent },
      { path: 'onboarding/step/2', component: StepProductsTypeComponent },
      { path: 'onboarding/step/3', component: LocationStepComponent },
      { path: 'onboarding/step/4-fixed', component: StepContextFixedComponent },
      {
        path: 'onboarding/step/4-itinerant',
        component: StepContextItinerantComponent,
      },
      { path: 'onboarding/step/5', component: StepProductsConfigComponent },
      { path: 'onboarding/step/6', component: ResumeEtPotentielComponent },
    ],
  },

  // Mode Kiosk - SANS header et footer applicatif
  {
    path: 'kiosk',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./kiosk/kiosk.routes').then((m) => m.KIOSK_ROUTES),
  },

  // Profil (nouveau)
  /*{
    path: 'profile',
    component: ProfileComponent,
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'general', pathMatch: 'full' },
      { path: 'general', component: ProfileGeneralComponent },
      { path: 'preferences', component: ProfilePreferencesComponent },
      { path: 'notifications', component: ProfileNotificationsComponent },
    ]
  },*/

  { path: '**', redirectTo: '', pathMatch: 'full' },
];
